import usePageMeta, { breadcrumbGraph } from '../hooks/usePageMeta';
import LegalPage from './LegalPage';

const UPDATED = 'Last updated September 29, 2026';
const TERMS_URL = 'https://printablegraphpaperpdf.com/terms';

const TERMS_JSONLD = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      name: 'Terms and Conditions | Printable Graph Paper',
      url: TERMS_URL,
      inLanguage: 'en',
      description:
        'The terms that apply when you use Printable Graph Paper — acceptable use, downloads, intellectual property, disclaimers, and liability.',
      isPartOf: { '@id': 'https://printablegraphpaperpdf.com/' },
    },
    breadcrumbGraph('Terms and Conditions', TERMS_URL),
  ],
};

const SECTIONS = [
  {
    id: 'introduction',
    title: 'Introduction',
    body: (
      <>
        <p>
          These Terms and Conditions govern your use of the Printable Graph Paper website at
          printablegraphpaperpdf.com and the tools it provides. Please read them before using the
          site.
        </p>
        <p>
          Throughout these terms, &quot;the website&quot;, &quot;the service&quot;, and
          &quot;Printable Graph Paper&quot; refer to this site and its graph paper generators.
        </p>
      </>
    ),
  },
  {
    id: 'acceptance',
    title: 'Acceptance of Terms',
    body: (
      <>
        <p>
          By accessing or using the website, you agree to be bound by these Terms and Conditions
          and by our <a href="/privacy">Privacy Policy</a>. If you do not agree with them, do not
          use the website.
        </p>
        <p>
          You must be at least 13 years old, or the age of digital consent in your jurisdiction —
          whichever is higher — to use the website.
        </p>
      </>
    ),
  },
  {
    id: 'use',
    title: 'Use of the Website',
    body: (
      <>
        <p>
          The website is offered for your personal and lawful use. You agree to use it in a way
          that does not infringe the rights of, or interfere with the use of the site by, anyone
          else.
        </p>
        <ul>
          <li>
            Do not interfere with the operation or security of the site, or attempt to gain
            unauthorized access to it.
          </li>
          <li>Do not use the site for any unlawful, fraudulent, or misleading purpose.</li>
          <li>
            Do not scrape, resell, or republish the site or its output at scale without written
            permission.
          </li>
          <li>Do not misrepresent your affiliation with Printable Graph Paper.</li>
        </ul>
        <p>We may suspend or restrict access to anyone who violates these rules.</p>
      </>
    ),
  },
  {
    id: 'generation',
    title: 'Graph Paper Generation and Downloads',
    body: (
      <>
        <p>
          The website generates graph paper sheets in your browser. You may download, save, and
          print the sheets you create for personal, non-commercial use, subject to these terms.
        </p>
        <p>
          Grid dimensions are provided as a convenience. Printer scaling, paper size settings, and
          browser print behaviour can change the physical size of a printed sheet — always verify
          critical measurements with a physical ruler before relying on them.
        </p>
        <div className="page-note">
          <span className="page-note-label">Please note</span>
          <p>
            If you need certified or contractually precise dimensions, treat printed output as a
            draft and confirm it against a known standard.
          </p>
        </div>
      </>
    ),
  },
  {
    id: 'intellectual-property',
    title: 'Intellectual Property',
    body: (
      <>
        <p>
          The website — including its design, text, graphics, logos, code, and software — is owned
          by Printable Graph Paper or its licensors and is protected by copyright and other
          intellectual property laws.
        </p>
        <p>
          Apart from the limited right to download and print sheets described above, you may not
          copy, modify, distribute, sell, or reverse engineer any part of the website without our
          prior written permission.
        </p>
        <p>
          If you send us suggestions or feedback, we may use them to improve the service without
          any obligation to you or compensation.
        </p>
      </>
    ),
  },
  {
    id: 'responsibilities',
    title: 'User Responsibilities',
    body: (
      <>
        <p>
          You are responsible for your use of the website and for anything you send to us, such as
          messages through the <a href="/contact">contact page</a>. You agree not to submit content
          that is unlawful, defamatory, infringing, or malicious.
        </p>
        <p>
          You are also responsible for your own devices and browser — download files only from
          sources you trust.
        </p>
      </>
    ),
  },
  {
    id: 'availability',
    title: 'Availability of the Service',
    body: (
      <>
        <p>
          The website is provided free of charge and on an &quot;as available&quot; basis. We may
          modify, suspend, or discontinue any part of the service at any time, with or without
          notice.
        </p>
        <p>We do not guarantee that the site will be uninterrupted, error-free, or available in every region.</p>
        <div className="page-note">
          <span className="page-note-label">Good to know</span>
          <p>
            The generator runs in your browser, so once the page has loaded, creating and printing
            sheets works even if our servers are unreachable.
          </p>
        </div>
      </>
    ),
  },
  {
    id: 'third-party',
    title: 'Third-Party Services',
    body: (
      <>
        <p>
          The website uses third-party services — Google Analytics to understand aggregate usage,
          and Google Fonts to serve the typeface used on this site. Those providers process data
          under their own privacy policies.
        </p>
        <p>
          The site may also link to external pages. We do not control and are not responsible for
          the content or practices of any third-party websites.
        </p>
      </>
    ),
  },
  {
    id: 'disclaimer',
    title: 'Disclaimer',
    body: (
      <>
        <p>
          To the maximum extent permitted by applicable law, the website and its content are
          provided &quot;as is&quot; and &quot;as available&quot;, without warranties of any kind,
          whether express or implied — including implied warranties of merchantability, fitness for
          a particular purpose, and non-infringement.
        </p>
        <p>
          We do not warrant that the grids, dimensions, or downloads will meet your requirements,
          or that the site will be free of errors or harmful components.
        </p>
        <p>Nothing in these terms excludes or limits any liability that cannot be excluded under applicable law.</p>
      </>
    ),
  },
  {
    id: 'liability',
    title: 'Limitation of Liability',
    body: (
      <>
        <p>
          To the maximum extent permitted by applicable law, Printable Graph Paper and its
          operators shall not be liable for any indirect, incidental, special, consequential, or
          punitive damages, or for any loss of profits, data, use, or goodwill, arising from:
        </p>
        <ul>
          <li>your access to or use of the website;</li>
          <li>your inability to access or use the website;</li>
          <li>any content obtained through the website; or</li>
          <li>unauthorized access, use, or alteration of your transmissions.</li>
        </ul>
        <p>
          Your sole remedy is to stop using the website. Where such limitation is permitted, our
          total liability for all claims relating to the site shall not exceed the greater of
          USD 100 or the amount you have paid us in the twelve months before the claim — which,
          since the service is free, will usually be nothing.
        </p>
      </>
    ),
  },
  {
    id: 'changes',
    title: 'Changes to These Terms',
    body: (
      <>
        <p>
          We may update these terms from time to time. When we do, we revise the &quot;Last
          updated&quot; date at the top of this page. Continued use of the website after changes
          means you accept them.
        </p>
        <p>If you do not agree with the updated terms, stop using the website.</p>
      </>
    ),
  },
  {
    id: 'contact',
    title: 'Contact',
    body: (
      <p>
        Questions about these Terms and Conditions? Email us at{' '}
        <a href="mailto:printablegraphpaper@gmail.com">printablegraphpaper@gmail.com</a> or use
        the <a href="/contact">contact page</a>.
      </p>
    ),
  },
];

function Terms() {
  usePageMeta({
    title: 'Terms and Conditions | Printable Graph Paper',
    description:
      'The terms that apply when you use Printable Graph Paper — acceptable use, downloads, intellectual property, disclaimers, and liability.',
    keywords: 'terms, terms and conditions, printable graph paper terms, legal, disclaimer',
    canonical: 'https://printablegraphpaperpdf.com/terms',
    ogTitle: 'Terms and Conditions | Printable Graph Paper',
    ogDescription:
      'How Printable Graph Paper may be used, what you can expect from the service, and what we ask in return.',
    ogUrl: 'https://printablegraphpaperpdf.com/terms',
    jsonLd: TERMS_JSONLD,
  });

  return (
    <LegalPage
      eyebrow="Legal"
      title="Terms and Conditions"
      leads={[
        'These terms describe how Printable Graph Paper may be used, what you can expect from us, and what we ask in return — in plain language wherever possible.',
      ]}
      meta={
        <>
          <span className="page-chip">
            <span className="page-chip-dot" />
            {UPDATED}
          </span>
          <span className="page-chip">Applies to the whole site</span>
        </>
      }
      sections={SECTIONS}
      cta={{
        eyebrow: 'Questions',
        title: 'Not sure about something?',
        body: 'If any of these terms are unclear, ask us — we will explain in plain language.',
        primary: { label: 'Contact us', href: '/contact' },
        secondary: { label: 'Privacy policy', href: '/privacy' },
      }}
    />
  );
}

export default Terms;
